<script setup lang="ts">
import { Vue3Lottie } from "vue3-lottie";
import { PURCHASE_LINKS } from "~/lib/book-purchase";

const { locale } = useI18n();

const lottieTitles = {
  it: {
    desktop: "/lotties/hero-banner-title-it.json",
    mobile: "/lotties/hero-banner-title-it-mobile.json",
  },
  "pt-BR": {
    desktop: "/lotties/hero-banner-title.json",
    mobile: "/lotties/hero-banner-title-mobile.json",
  },
};

const banners = {
  it: {
    desktop: "/images/elements/a-arte-da-influencia-livro-comprar-preco-it.png",
    mobile:
      "/images/elements/a-arte-da-influencia-livro-comprar-preco-it-mobile.png",
  },
  "pt-BR": {
    desktop: "/images/elements/a-arte-da-influencia-livro-comprar-preco.png",
    mobile:
      "/images/elements/a-arte-da-influencia-livro-comprar-preco-mobile.png",
  },
};

const lottieTitle = computed(() => {
  return (
    lottieTitles[locale.value as keyof typeof lottieTitles] ??
    lottieTitles["pt-BR"]
  );
});

const banner = computed(() => {
  return banners[locale.value as keyof typeof banners] ?? banners["pt-BR"];
});

const livrariasUrl = PURCHASE_LINKS.livrariasCuritiba;
</script>

<template>
  <UtilsColoredSeparator />

  <header
    class="relative flex flex-col items-center justify-center overflow-hidden bg-[url(/images/backgrounds/light-blue-bg.jpg)] bg-cover bg-[center_top] pt-[68vw] pb-16 md:pt-[14vw] md:pb-10"
  >
    <div
      class="absolute top-5 z-30 flex w-full justify-end container max-w-screen-xl px-4"
    >
      <UtilsLanguageSelector />
    </div>

    <ClientOnly>
      <div class="pointer-events-none absolute top-0 hidden w-[52%] md:block">
        <Vue3Lottie
          :animationLink="lottieTitle.desktop"
          height="auto"
          width="100%"
        />
      </div>
      <div class="pointer-events-none absolute top-8 block w-full md:hidden">
        <Vue3Lottie
          :animationLink="lottieTitle.mobile"
          height="auto"
          width="100%"
        />
      </div>
      <div class="pointer-events-none absolute top-8 hidden w-[82%] md:block">
        <Vue3Lottie
          animationLink="/lotties/hero-banner-elements.json"
          height="auto"
          width="100%"
        />
      </div>
    </ClientOnly>

    <h1 class="sr-only">
      {{ $t("book-title") }} — {{ $t("hero.tagline") }}
    </h1>

    <picture class="relative z-10">
      <source media="(min-width: 768px)" :srcset="banner.desktop" />
      <img
        :src="banner.mobile"
        :alt="$t('book-title')"
        class="relative z-10 w-full drop-shadow-2xl md:h-[34vw] md:w-auto"
      />
    </picture>

    <div
      class="relative z-20 mt-6 flex w-full max-w-xl flex-col items-center gap-3 px-4 sm:flex-row sm:justify-center md:absolute md:bottom-16 md:mt-0"
    >
      <UtilsButton
        bordered
        as="a"
        href="#buy-now"
        scroll-to="buy-now"
        class="w-full border-white text-white hover:border-white hover:text-white sm:w-auto"
      >
        {{ $t("buy-now.button") }}
        <template #append>
          <Icon name="ic:round-arrow-right" class="ml-1 text-2xl" />
        </template>
      </UtilsButton>

      <a
        :href="livrariasUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="flex w-full items-center justify-center gap-3 rounded-lg border border-white/30 bg-white px-4 py-2.5 text-sm font-semibold text-[#003B71] shadow-lg transition hover:bg-white/95 sm:w-auto"
      >
        <img
          src="/images/partners/livrarias-curitiba.png"
          alt="Livrarias Curitiba"
          class="h-8 w-auto object-contain"
        />
        <span class="hidden sm:inline">{{ $t("hero.livrarias-cta") }}</span>
      </a>
    </div>
  </header>
</template>
